import React, { createContext, useContext } from "react";
import { usePatient } from "./PatientProvider";
import { AuthContext } from "../context/AuthContext";
import { useLoader } from "./LoaderProvider";
import { useAssessment } from "./AssessmentProvider";
import { PrintContext, CHECKS } from "../context/PrintContext";

// Create the context
const PatientAndAssessmentDataHelperContext = createContext();
let bodyAreasSubSectionIndex = {
  truncal: 2,
  extremity: 1,
  facialAndOral: 0,
};
// Create a provider component
export const PatientAndAssessmentDataHelperProvider = ({ children }) => {
  const { handlePatientData } = usePatient();
  const { makeAuthenticatedApiCall } = useContext(AuthContext);
  let { handleAssessmentToggle, handleNotes } = useAssessment();
  const { markAsDone, markAsError } = React.useContext(PrintContext);

  const { handleLoader } = useLoader();

  const getPatientData = async (patientId) => {
    handleLoader(true);
    const addBy = localStorage.getItem("addedThroughSource");
    let url = `/api/userservice/getpatientdetails/${patientId}`;
    if (addBy) {
      url += `?addedThrough=${encodeURIComponent(addBy)}`;
    }

    let response = await makeAuthenticatedApiCall("get", url, {});
    handleLoader(false);
    if (response && response.data) {
      handlePatientData(response.data);
      markAsDone(CHECKS.PATIENT_DETAILS_ERR);
    } else {
      markAsError(CHECKS.PATIENT_DETAILS_ERR, response.status);
      handlePatientData(null);
    }
  };

  const handleExistingResponses = (key, responses, subSectionIndex) => {
    responses.forEach((response) => {
      let questionNumber = response.question - 1;
      let answer = response.answerOption;
      if (answer === true) {
        answer = "Yes";
      } else if (answer === false) {
        answer = "No";
      }
      handleAssessmentToggle(key, subSectionIndex, questionNumber, answer);
    });
  };

  const handleContinueAssessmentData = (data) => {
    let {
      score,
      sectionScores,
      assessmentDate,
      firstAssessmentDate,
      lastAssessmentDate,
      totalAssessments,
      assessmentData,
    } = data;
    let keys = Object.keys(assessmentData);
    keys.forEach((key) => {
      if (key === "bodyAreas") {
        let subSections = Object.keys(assessmentData[key]);
        subSections.forEach((subSection) => {
          let responses = assessmentData[key][subSection]["responses"];
          let subSectionIndex = bodyAreasSubSectionIndex[subSection];
          handleExistingResponses(key, responses, subSectionIndex);
          // Notes
          let notes = assessmentData[key][subSection]["notes"];
          handleNotes(key, subSectionIndex, notes);
        });
      } else {
        let subSectionIndex = 0;
        let responses = assessmentData[key]["responses"];
        handleExistingResponses(key, responses, subSectionIndex);
        // Notes
        let notes = assessmentData[key]["notes"];
        handleNotes(key, subSectionIndex, notes);
      }
    });

    return {
      score: score,
      sectionScores: sectionScores,
      selectedTab: data.status - 1,
      firstAssessmentDate: firstAssessmentDate,
      lastAssessmentDate: lastAssessmentDate,
      totalAssessments: totalAssessments,
      assessmentDate: assessmentDate,
    };
  };
  const getAssessmentDetails = async (patientId, assessmentId) => {
    let returnItem = {
      status: false,
      data: null,
    };
    handleLoader(true);
    let response = await makeAuthenticatedApiCall(
      "get",
      `/api/userservice/getAssessments/${patientId}/${assessmentId}`,
      {}
    );
    handleLoader(false);
    if (response && response.status === 200 && response.data) {
      returnItem = {
        status: true,
        data: handleContinueAssessmentData(response.data),
      };
      markAsDone(CHECKS.ASSESSMENTS_ERR);
    } else {
      markAsError(CHECKS.ASSESSMENTS_ERR, response.status);
    }
    return returnItem;
  };
  const saveDoNotShowAssessmentWalkthrough = async (value) => {
    await makeAuthenticatedApiCall(
      "post",
      `/api/userservice/saveDoNotShowAssessmentWalkthrough`,
      {
        doNotShowAssessmentWalkthrough: value,
      }
    );
  };
  const checkUserDetails = async () => {
    handleLoader(true);
    let response = await makeAuthenticatedApiCall(
      "get",
      `/api/userservice/userdetails`,
      {
        doNotShowAssessmentWalkthrough: true,
      }
    );
    // handleLoader(false);
    return response;
  };

  return (
    <PatientAndAssessmentDataHelperContext.Provider
      value={{
        getPatientData,
        getAssessmentDetails,
        saveDoNotShowAssessmentWalkthrough,
        checkUserDetails,
      }}
    >
      {children}
    </PatientAndAssessmentDataHelperContext.Provider>
  );
};

export const usePatientAndAssessmentDataHelper = () =>
  useContext(PatientAndAssessmentDataHelperContext);
